import { ElLoading } from 'element-plus'
import { useCookie } from '#imports'

type LoadingToken = { key: any }
const __fullscreenKey = Symbol('fullscreen')
const __pools = new Map<any, { instance: any, count: number, timer: any, startedAt: number }>()

const __resolveTarget = (opt?: any): Element | null => {
  if (!opt || typeof window === 'undefined') return null
  const t = opt.target
  if (!t) return null
  if (typeof t === 'string') return document.querySelector(t)
  try {
    if (t instanceof Element) return t as Element
  } catch {}
  return null
}

const __startLoading = (opt?: any): LoadingToken | null => {
  if (typeof window === 'undefined') return null
  const targetEl = __resolveTarget(opt)
  const key = targetEl ?? __fullscreenKey
  let pool = __pools.get(key)
  if (!pool) {
    pool = { instance: null, count: 0, timer: null, startedAt: 0 }
    __pools.set(key, pool)
  }
  pool.count += 1
  if (pool.count === 1) {
    const delay = opt && typeof opt === 'object' && opt.delay ? Number(opt.delay) : 0
    const create = () => {
      const text = opt && typeof opt === 'object' && opt.message ? opt.message : '加载中...'
      const lock = opt && typeof opt === 'object' && 'lock' in opt ? !!opt.lock : true
      pool!.instance = ElLoading.service({ fullscreen: !targetEl, text, target: targetEl ?? undefined, lock })
      pool!.startedAt = Date.now()
    }
    if (delay > 0) {
      clearTimeout(pool.timer)
      pool.timer = setTimeout(create, delay)
    } else {
      create()
    }
  }
  return { key }
}

const __stopLoading = (token?: LoadingToken) => {
  if (typeof window === 'undefined' || !token) return
  const pool = __pools.get(token.key)
  if (!pool) return
  pool.count = Math.max(pool.count - 1, 0)
  if (pool.count === 0) {
    clearTimeout(pool.timer)
    pool.timer = null
    if (pool.instance) {
      pool.instance.close()
      pool.instance = null
    }
    __pools.delete(token.key)
  }
}

export const useApi = () => {
  const config = useRuntimeConfig()
  const baseURL = config.public.apiBase
  const request = async <T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', url: string, dataOrOptions?: any, maybeOptions?: any) => {
    let options: any = {}
    if (maybeOptions) options = { ...maybeOptions }
    if (dataOrOptions !== undefined) {
      const isOptionsLike = typeof dataOrOptions === 'object' && (
        ('body' in dataOrOptions) || ('query' in dataOrOptions) || ('headers' in dataOrOptions) || ('method' in dataOrOptions)
      )
      if (isOptionsLike) {
        options = { ...options, ...dataOrOptions }
      } else {
        options = method === 'GET' ? { ...options, query: dataOrOptions } : { ...options, body: dataOrOptions }
      }
    }
    const hdrs = { ...(options.headers || {}) }
    try {
      let token: string | null = null
      // Prefer cookie (works in SSR and CSR)
      const c1 = useCookie<string | undefined>('Token')
      const c2 = useCookie<string | undefined>('token')
      const c3 = useCookie<string | undefined>('authToken')
      token = c1?.value || c2?.value || c3?.value || null
      // Fallback to localStorage or sessionStorage on client
      if (!token && typeof window !== 'undefined') {
        token = useAuth().getToken();
      }
      if (token) {
        if (!hdrs.Authorization) hdrs.Authorization = token
        if (!hdrs.token) hdrs.token = token
      }
    } catch {}
    options.headers = hdrs
    const useLoading = !!options.loading
    const __token = useLoading ? __startLoading(options.loading === true ? {} : options.loading) : null
    let result: any
    try {
      if (typeof window === 'undefined' && process.env.NODE_ENV !== 'production') {
        console.log('[SSR FETCH]', method, `${baseURL}${url}`)
        if (options?.query) console.log('[SSR QUERY]', JSON.stringify(options.query))
        if (options?.body) console.log('[SSR BODY]', JSON.stringify(options.body))
        if (options?.headers) console.log('[SSR HEADERS]', JSON.stringify(options.headers))
      }
      const v = await $fetch<any>(url, { baseURL, method, ...options })
      const code = v?.code
      const msg = v?.msg ?? v?.message
      const hasCode = !!v && typeof v === 'object' && 'code' in v
      const ok = hasCode ? (code === '00000' || code === 0 || code === 200) : true
      const data = (v && v.data !== undefined) ? (v.data as T) : (v as T)
      result = { ok, code, msg, data, raw: v, error: null }
      if (typeof window === 'undefined' && process.env.NODE_ENV !== 'production') {
        let size: number | undefined = undefined
        const d: any = data as any
        if (Array.isArray(d)) size = d.length
        else if (Array.isArray(d?.records)) size = d.records.length
        else if (Array.isArray(d?.list)) size = d.list.length
        const preview = (() => { try { return JSON.stringify(d).slice(0, 200) } catch { return '' } })()
        console.log('[SSR FETCH OK]', method, `${baseURL}${url}`, 'size:', size ?? '-', preview)
      }
    } catch (e: any) {
      result = { ok: false, code: undefined, msg: e?.message, data: undefined as any, raw: undefined, error: e }
      if (typeof window === 'undefined' && process.env.NODE_ENV !== 'production') {
        console.error('[SSR FETCH ERR]', method, `${baseURL}${url}`, e?.message)
      }
    }
    if (useLoading) __stopLoading(__token as LoadingToken)
    return result
  }
  const get = <T>(url: string, query?: any, options?: any) => request<T>('GET', url, query, options)
  const post = <T>(url: string, body?: any, options?: any) => request<T>('POST', url, body, options)
  const put = <T>(url: string, body?: any, options?: any) => request<T>('PUT', url, body, options)
  const del = <T>(url: string, query?: any, options?: any) => request<T>('DELETE', url, query, options)
  return { baseURL, get, post, put, del }
}
